import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarDays, ChevronLeft, ChevronRight, Clock, GripVertical, Plus, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useAppointmentMutations,
  useAppointments,
  useAvailabilitySlots,
  useCurrentPermissions,
  useFacilities,
  useGymMembers,
  useLeads,
  useMembers,
} from '@/api/queries'
import type { Appointment, AppointmentKind, Uuid } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { APPOINTMENT_KIND_LABELS } from '@/lib/format'
import { notify } from '@/lib/toast'

/**
 * L'agenda — F6, AGD-03.
 *
 * ## Una settimana, e un operatore per volta
 *
 * La griglia è per **giorno della settimana**, perché è così che si guarda un'agenda:
 * «cosa c'è giovedì» è la domanda, non «cosa c'è nei prossimi trenta giorni». Un
 * operatore per volta e non tutti insieme, perché sette colonne di persone diverse su
 * uno schermo da banco non si leggono — il selettore in cima è il gesto che si fa
 * comunque prima di prenotare («chi lo segue?»).
 *
 * ## Le disdette si vedono
 *
 * Non le nasconde, le barra: un'agenda che le nasconde non spiega perché un'ora è
 * libera, e chi guarda il giovedì vuole sapere se quell'ora era di qualcuno.
 *
 * ## Trascinare, e il 409 che va detto
 *
 * Un appuntamento si sposta trascinandolo: la colonna del giorno si trasforma in un
 * **righello di mezz'ore** mentre si trascina, e lasciarlo su una di quelle sposta
 * giorno e ora insieme. Il pezzo che conta non è il trascinamento ma la risposta: il
 * `PATCH` può rispondere `409` con dentro **l'identificativo di ciò che occupa quell'ora**,
 * e un drag & drop che ignora quel dettaglio rimette la scheda dove l'ha presa senza dire
 * perché — cioè sembra rotto. Qui il messaggio nomina chi c'è già, quando lo conosce.
 *
 * Accanto c'è **«Sposta»**, che fa la stessa cosa da tastiera: un gesto disponibile solo
 * col mouse esclude chi non lo usa, e questo è un pannello da lavoro.
 *
 * ## Gli spazi liberi li chiede al server
 *
 * Il dialogo di prenotazione non calcola niente: chiede gli slot, che il server ricava
 * sottraendo assenze, appuntamenti **e le lezioni che l'operatore insegna**. Quel terzo
 * pezzo il pannello non l'ha in mano, e ricalcolare gli altri due qui vorrebbe dire
 * avere due risposte diverse alla stessa domanda.
 */
export const Route = createFileRoute('/_app/agenda/')({
  component: AgendaPage,
})

/** L'elenco vive in `lib/format.ts` da quando la home ne mostra le stesse righe. */
const KIND_LABEL = APPOINTMENT_KIND_LABELS

/** Il lunedì della settimana che contiene `date`, in locale. */
function mondayOf(date: Date): Date {
  const value = new Date(date)
  value.setHours(0, 0, 0, 0)
  const shift = (value.getDay() + 6) % 7
  value.setDate(value.getDate() - shift)
  return value
}

function addDays(date: Date, days: number): Date {
  const value = new Date(date)
  value.setDate(value.getDate() + days)
  return value
}

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

function dayLabel(date: Date): string {
  return date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** `YYYY-MM-DDTHH:MM` per un `input[type=datetime-local]`, in ora locale. */
function localInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Il righello che compare mentre si trascina — AGD-03.
 *
 * Mezz'ore e non ore: un personal alle 9:30 è la normalità, e un righello a ore avrebbe
 * costretto a correggere l'orario dopo averlo spostato, cioè a fare due gesti per uno.
 * Dalle 7 alle 22 perché una palestra non prenota alle 3 di notte, e un righello che
 * copre le ventiquattro ore rende minuscolo il pezzo che si usa.
 */
const RULER_FROM_HOUR = 7
const RULER_TO_HOUR = 22
const RULER_STEP_MINUTES = 30

const RULER: { hour: number; minute: number; label: string }[] = []
for (let hour = RULER_FROM_HOUR; hour < RULER_TO_HOUR; hour += 1) {
  for (let minute = 0; minute < 60; minute += RULER_STEP_MINUTES) {
    RULER.push({
      hour,
      minute,
      label: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    })
  }
}

/** Lo spostamento conserva la **durata**: si sposta un appuntamento, non lo si riscrive. */
function movedTo(appointment: Appointment, day: Date, hour: number, minute: number) {
  const durationMs =
    new Date(appointment.endsAt).getTime() - new Date(appointment.startsAt).getTime()
  const startsAt = new Date(day)
  startsAt.setHours(hour, minute, 0, 0)
  return {
    startsAt: startsAt.toISOString(),
    endsAt: new Date(startsAt.getTime() + durationMs).toISOString(),
  }
}

function AgendaPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('appointment:read')
  const canWrite = permissions.has('appointment:write')

  const [monday, setMonday] = useState(() => mondayOf(new Date()))
  const [professionalMemberId, setProfessional] = useState<Uuid | undefined>(undefined)
  /**
   * «Per sala» invece di «per operatore» — AGD-03.
   *
   * Non è un secondo filtro accanto al primo: è **la stessa griglia raggruppata per
   * un'altra cosa**, perché le due domande sono alternative. «Chi è occupato giovedì»
   * la fa chi prenota, «cos'è occupato giovedì» la fa chi cerca una sala libera, e
   * nessuno le fa insieme. Mostrarle come due filtri componibili avrebbe suggerito una
   * terza vista che non esiste.
   */
  const [groupBy, setGroupBy] = useState<'professional' | 'facility'>('professional')
  const [booking, setBooking] = useState(false)
  /**
   * L'appuntamento che si sta trascinando. Sta qui e non nel `dataTransfer` perché serve
   * a **due** cose: sapere cosa spostare al rilascio, e far comparire il righello in
   * tutte le colonne mentre il gesto è in corso — il `dataTransfer` si può leggere solo
   * nell'evento di rilascio, quindi da solo non basterebbe.
   */
  const [dragging, setDragging] = useState<Appointment | null>(null)
  const [moving, setMoving] = useState<Appointment | null>(null)

  const mutations = useAppointmentMutations()
  const staff = useMembers({ status: 'active' })
  const facilities = useFacilities()
  const from = monday.toISOString()
  const to = addDays(monday, 7).toISOString()
  const appointments = useAppointments(
    { from, to, ...(professionalMemberId === undefined ? {} : { professionalMemberId }) },
    canRead,
  )

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(monday, index)),
    [monday],
  )

  /** I nomi delle sale, per non mostrare un `uuid` a chi cerca uno spazio libero. */
  const facilityNames = useMemo(
    () => new Map((facilities.data?.data ?? []).map((row) => [row.id, row.name])),
    [facilities.data],
  )

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>()
    for (const row of appointments.data?.data ?? []) {
      const key = new Date(row.startsAt).toDateString()
      const list = map.get(key)
      if (list === undefined) map.set(key, [row])
      else list.push(row)
    }
    return map
  }, [appointments.data])

  /**
   * Sposta un appuntamento, e **dice chi occupa l'ora** quando il server rifiuta.
   *
   * Il `409` porta `details.appointmentId`: se quell'appuntamento è fra quelli della
   * settimana caricata si può nominare la persona, che è la differenza fra «non si può»
   * e «c'è già Rossi alle 10». Quando non lo conosciamo — è in un'altra settimana, o è
   * di un operatore filtrato via — resta il messaggio del server, che dice almeno *cosa*
   * è occupato.
   */
  async function move(appointment: Appointment, patch: { startsAt: string; endsAt: string }) {
    try {
      await mutations.update.mutateAsync({ id: appointment.id, patch })
      notify({
        message: `Spostato a ${new Date(patch.startsAt).toLocaleString('it-IT', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}.`,
        variant: 'success',
      })
    } catch (cause) {
      const clashId =
        cause instanceof ApiError && typeof cause.details?.['appointmentId'] === 'string'
          ? cause.details['appointmentId']
          : null
      const clash =
        clashId === null
          ? undefined
          : (appointments.data?.data ?? []).find((row) => row.id === clashId)
      notify({
        message:
          clash === undefined
            ? errorMessage(cause)
            : `${errorMessage(cause)} C’è ${clash.subjectName} alle ${hhmm(clash.startsAt)}.`,
        variant: 'danger',
      })
    }
  }

  if (!canRead) {
    return (
      <>
        <PageHeader title="Agenda" description="Gli appuntamenti della settimana." />
        <Alert variant="neutral" title="Serve «appointment:read»" icon={false}>
          Il tuo ruolo non vede l’agenda.
        </Alert>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Agenda"
        description="Una settimana per volta, un operatore per volta: è come si guarda un’agenda al banco."
      />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMonday(addDays(monday, -7))}
            aria-label="Settimana precedente"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMonday(mondayOf(new Date()))}>
            Questa settimana
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMonday(addDays(monday, 7))}
            aria-label="Settimana successiva"
          >
            <ChevronRight className="size-4" />
          </Button>
          <p className="text-muted-foreground ml-2 text-sm">
            {dayLabel(monday)} – {dayLabel(addDays(monday, 6))}
          </p>
        </div>

        <div className="flex items-end gap-2">
          <Field label="Raggruppa per" className="w-40">
            {(props) => (
              <Select
                {...props}
                value={groupBy}
                onValueChange={(value) =>
                  setGroupBy(value === 'facility' ? 'facility' : 'professional')
                }
                aria-label="Raggruppa la giornata per operatore o per sala"
                items={[
                  { value: 'professional', label: 'Operatore' },
                  {
                    value: 'facility',
                    label: (
                      <span className="inline-flex items-center gap-2">
                        Sala
                        <span className="text-muted-foreground text-xs">
                          chi cerca uno spazio libero
                        </span>
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </Field>
          <Field label="Operatore" className="w-56">
            {(props) => (
              <Select
                {...props}
                value={professionalMemberId}
                onValueChange={setProfessional}
                aria-label="Filtra per operatore"
                placeholder="Tutti"
                items={(staff.data?.data ?? []).map((member) => ({
                  value: member.memberId,
                  label: (
                    <span className="inline-flex items-center gap-2">
                      {member.name ?? member.email}
                      <span className="text-muted-foreground text-xs">{member.role}</span>
                    </span>
                  ),
                }))}
              />
            )}
          </Field>
          {professionalMemberId !== undefined ? (
            <Button size="sm" variant="ghost" onClick={() => setProfessional(undefined)}>
              <X className="size-4" />
            </Button>
          ) : null}
          {canWrite ? (
            <Button size="sm" variant="primary" onClick={() => setBooking(true)}>
              <Plus className="size-4" />
              Fissa
            </Button>
          ) : null}
        </div>
      </div>

      {appointments.isError && !appointments.data ? (
        <ErrorState
          message={errorMessage(appointments.error)}
          onRetry={() => void appointments.refetch()}
        />
      ) : appointments.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="grid gap-3 lg:grid-cols-7">
          {days.map((day) => {
            const rows = (byDay.get(day.toDateString()) ?? []).sort((a, b) => {
              if (groupBy === 'facility') {
                // Le sale insieme, e dentro ogni sala per ora: e' la forma della
                // domanda «cos'e' occupato giovedi'».
                const left = facilityNames.get(a.facilityId ?? '') ?? 'zzz'
                const right = facilityNames.get(b.facilityId ?? '') ?? 'zzz'
                if (left !== right) return left < right ? -1 : 1
              }
              return a.startsAt < b.startsAt ? -1 : 1
            })
            return (
              <section
                key={day.toISOString()}
                className="border-border bg-card rounded-lg border p-3"
              >
                <h2 className="mb-2 text-xs font-semibold tracking-wide uppercase">
                  {dayLabel(day)}
                </h2>
                {rows.length === 0 && dragging === null ? (
                  <p className="text-muted-foreground text-xs">—</p>
                ) : (
                  <ul className="space-y-2">
                    {rows.map((row) => (
                      <AppointmentCard
                        key={row.id}
                        appointment={row}
                        canWrite={canWrite}
                        draggable={canWrite && row.status !== 'cancelled'}
                        onDragStart={() => setDragging(row)}
                        onDragEnd={() => setDragging(null)}
                        onMove={() => setMoving(row)}
                        // Guardando per sala, il nome che serve e' quello della sala: chi
                        // cerca uno spazio libero non sta cercando una persona.
                        facilityName={
                          groupBy === 'facility'
                            ? (facilityNames.get(row.facilityId ?? '') ?? 'senza sala')
                            : undefined
                        }
                      />
                    ))}
                  </ul>
                )}
                {dragging !== null ? (
                  <DayRuler
                    day={day}
                    onDrop={(hour, minute) => {
                      const target = dragging
                      setDragging(null)
                      if (target === null) return
                      void move(target, movedTo(target, day, hour, minute))
                    }}
                  />
                ) : null}
              </section>
            )
          })}
        </div>
      )}

      {moving !== null ? (
        <MoveDialog
          appointment={moving}
          onClose={() => setMoving(null)}
          onConfirm={(patch) => {
            setMoving(null)
            void move(moving, patch)
          }}
        />
      ) : null}

      {booking ? (
        <BookDialog
          staff={(staff.data?.data ?? []).map((member) => ({
            id: member.memberId,
            label: member.name ?? member.email,
            role: member.role,
          }))}
          initialProfessional={professionalMemberId}
          onClose={() => setBooking(false)}
        />
      ) : null}
    </>
  )
}

/**
 * Il righello di mezz'ore, che compare solo mentre si trascina.
 *
 * Solo mentre: sette colonne con trenta righe ciascuna sono illeggibili a riposo, e
 * l'agenda si guarda molto più spesso di quanto si riordini. Comparire al gesto è anche
 * ciò che rende evidente **dove si può lasciare** la scheda, senza istruzioni.
 */
function DayRuler({ day, onDrop }: { day: Date; onDrop: (hour: number, minute: number) => void }) {
  const [over, setOver] = useState<string | null>(null)

  return (
    <ul className="border-border mt-2 space-y-0.5 border-t pt-2" aria-hidden>
      {RULER.map((slot) => (
        <li key={slot.label}>
          <div
            // `preventDefault` su `dragOver` è ciò che rende un elemento un bersaglio
            // valido: senza, il browser rifiuta il rilascio e il gesto sembra non fare
            // niente.
            onDragOver={(event) => {
              event.preventDefault()
              setOver(slot.label)
            }}
            onDragLeave={() => setOver((current) => (current === slot.label ? null : current))}
            onDrop={(event) => {
              event.preventDefault()
              setOver(null)
              onDrop(slot.hour, slot.minute)
            }}
            className={
              over === slot.label
                ? 'bg-primary-soft text-primary-content rounded px-1 py-0.5 text-[11px] tabular-nums'
                : 'text-muted-foreground rounded px-1 py-0.5 text-[11px] tabular-nums'
            }
          >
            {slot.label}
          </div>
        </li>
      ))}
      <li className="text-muted-foreground px-1 pt-1 text-[11px]">
        {day.toLocaleDateString('it-IT', { weekday: 'short' })}
      </li>
    </ul>
  )
}

/**
 * Spostare da tastiera, che è la stessa cosa del trascinamento con un'altra mano.
 *
 * Esiste perché un gesto disponibile solo col mouse esclude chi non lo usa, e perché al
 * banco capita di sapere già l'orario — digitarlo è più rapido che cercarlo in un
 * righello. Parte dall'orario attuale: si sposta un appuntamento, non se ne scrive uno.
 */
function MoveDialog({
  appointment,
  onClose,
  onConfirm,
}: {
  appointment: Appointment
  onClose: () => void
  onConfirm: (patch: { startsAt: string; endsAt: string }) => void
}) {
  const [value, setValue] = useState(() => localInputValue(new Date(appointment.startsAt)))
  const durationMinutes = Math.round(
    (new Date(appointment.endsAt).getTime() - new Date(appointment.startsAt).getTime()) / 60_000,
  )

  function confirm(): void {
    const startsAt = new Date(value)
    if (Number.isNaN(startsAt.getTime())) {
      notify({ message: 'Orario non valido.', variant: 'danger' })
      return
    }
    onConfirm({
      startsAt: startsAt.toISOString(),
      // La durata resta quella: cambiarla insieme allo spostamento sarebbe un secondo
      // gesto travestito da uno.
      endsAt: new Date(startsAt.getTime() + durationMinutes * 60_000).toISOString(),
    })
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sposta l’appuntamento</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-muted-foreground text-sm">
            {appointment.subjectName} · {KIND_LABEL[appointment.kind]} · {String(durationMinutes)}{' '}
            minuti
          </p>
          <Field
            label="Nuovo inizio"
            hint="La durata resta la stessa. L’orario fuori disponibilità è ammesso: dal banco si prenota anche fuori."
          >
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={confirm}>Sposta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AppointmentCard({
  appointment,
  canWrite,
  facilityName,
  draggable,
  onDragStart,
  onDragEnd,
  onMove,
}: {
  appointment: Appointment
  canWrite: boolean
  /** Presente solo nella vista per sala: la' il nome che conta e' quello dello spazio. */
  facilityName?: string
  /** Le disdette non si trascinano: un'ora liberata non si sposta, si riprenota. */
  draggable: boolean
  onDragStart: () => void
  onDragEnd: () => void
  onMove: () => void
}) {
  const mutations = useAppointmentMutations()
  const past = new Date(appointment.startsAt) < new Date()
  const cancelled = appointment.status === 'cancelled'

  async function setStatus(status: Appointment['status']): Promise<void> {
    try {
      if (status === 'cancelled') {
        // Il motivo è obbligatorio, e chiederlo dopo significa non averlo mai: se chi
        // disdice non scrive niente, non si manda una disdetta muta al server.
        const reason = window.prompt('Perché è stato disdetto?')?.trim()
        if (reason === undefined || reason === '') return
        await mutations.update.mutateAsync({
          id: appointment.id,
          patch: { status, cancelReason: reason },
        })
      } else {
        await mutations.update.mutateAsync({ id: appointment.id, patch: { status } })
      }
      notify({ message: 'Aggiornato.', variant: 'success' })
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  return (
    <li
      className={
        draggable
          ? 'border-border hover:border-primary rounded-md border p-2 text-sm'
          : 'border-border rounded-md border p-2 text-sm'
      }
      draggable={draggable}
      onDragStart={(event) => {
        // Un `dataTransfer` va impostato o Firefox non avvia il trascinamento; il dato
        // vero sta nello stato della pagina, perche' il righello deve comparire *durante*
        // il gesto e qui si legge solo al rilascio.
        event.dataTransfer.setData('text/plain', appointment.id)
        event.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragEnd={onDragEnd}
    >
      <p className="text-muted-foreground flex items-center gap-1 text-xs">
        {draggable ? <GripVertical className="size-3 cursor-grab" aria-hidden /> : null}
        <Clock className="size-3" />
        {hhmm(appointment.startsAt)}–{hhmm(appointment.endsAt)}
      </p>
      <p className={cancelled ? 'font-medium line-through' : 'font-medium'}>
        {appointment.subjectName}
      </p>
      <p className="text-muted-foreground text-xs">
        {facilityName === undefined
          ? KIND_LABEL[appointment.kind]
          : `${facilityName} · ${KIND_LABEL[appointment.kind]}`}
      </p>
      {appointment.professionalName !== null ? (
        <p className="text-muted-foreground text-xs">{appointment.professionalName}</p>
      ) : (
        // L'operatore che ha lasciato l'organizzazione: l'appuntamento resta, e dirlo
        // è meglio che mostrare una riga senza nessuno.
        <p className="text-muted-foreground text-xs italic">operatore non più in forza</p>
      )}
      {appointment.subjectPhone !== null ? (
        <a className="text-xs underline" href={`tel:${appointment.subjectPhone}`}>
          {appointment.subjectPhone}
        </a>
      ) : null}
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <StatusBadge status={appointment.status} />
        {appointment.cancelReason !== null ? (
          <span className="text-muted-foreground text-xs">{appointment.cancelReason}</span>
        ) : null}
      </div>
      {canWrite && !cancelled ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {/* «Fatto» e «non si è presentato» solo a cose passate: prima sarebbe una
              previsione, e il server risponderebbe 409. */}
          {past && appointment.status !== 'done' ? (
            <Button size="sm" variant="ghost" onClick={() => void setStatus('done')}>
              Fatto
            </Button>
          ) : null}
          {past && appointment.status !== 'no_show' ? (
            <Button size="sm" variant="ghost" onClick={() => void setStatus('no_show')}>
              Non venuto
            </Button>
          ) : null}
          {!past ? (
            <Button size="sm" variant="ghost" onClick={() => void setStatus('cancelled')}>
              Disdici
            </Button>
          ) : null}
          {/* La stessa cosa del trascinamento, da tastiera: un gesto solo col mouse
              esclude chi non lo usa, e questo e' un pannello da lavoro. */}
          {!past ? (
            <Button size="sm" variant="ghost" onClick={onMove}>
              Sposta
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function StatusBadge({ status }: { status: Appointment['status'] }) {
  if (status === 'confirmed') return <Badge variant="success">confermato</Badge>
  if (status === 'done') return <Badge variant="neutral">fatto</Badge>
  if (status === 'no_show') return <Badge variant="danger">non venuto</Badge>
  if (status === 'cancelled') return <Badge variant="warning">disdetto</Badge>
  return <Badge variant="secondary">fissato</Badge>
}

/**
 * Il dialogo di prenotazione, che parte dagli **spazi liberi** e non da un orario da
 * digitare: chi telefona chiede «quando può vedermi», e la risposta è un elenco.
 * Restano modificabili a mano, perché la disponibilità propone e non vieta — il
 * cliente che insiste per le 21 esiste, e il server lo accetta.
 */
function BookDialog({
  staff,
  initialProfessional,
  onClose,
}: {
  staff: { id: Uuid; label: string; role: string }[]
  initialProfessional?: Uuid
  onClose: () => void
}) {
  const mutations = useAppointmentMutations()
  const [professionalMemberId, setProfessional] = useState<Uuid | undefined>(initialProfessional)
  const [kind, setKind] = useState<AppointmentKind>('personal_training')
  const [durationMinutes, setDuration] = useState(60)
  const [subjectKind, setSubjectKind] = useState<'member' | 'lead'>('member')
  const [gymMemberId, setGymMember] = useState<Uuid | undefined>(undefined)
  const [leadId, setLead] = useState<Uuid | undefined>(undefined)
  const [startsAt, setStartsAt] = useState('')
  const [notes, setNotes] = useState('')

  // I primi cento iscritti attivi: il dialogo si apre dal banco con un nome in testa,
  // e una ricerca incrementale qui avrebbe voluto un campo in più in un dialogo che ne
  // ha già sei. Chi ne ha di più cerca dalla pagina degli iscritti e prenota da lì.
  const members = useGymMembers({ status: 'active', limit: 100 })
  const leads = useLeads({ limit: 100 })

  const searchFrom = useMemo(() => new Date().toISOString(), [])
  const searchTo = useMemo(() => addDays(new Date(), 14).toISOString(), [])
  const slots = useAvailabilitySlots(
    {
      professionalMemberId: professionalMemberId ?? '',
      from: searchFrom,
      to: searchTo,
      durationMinutes,
    },
    professionalMemberId !== undefined,
  )

  async function submit(): Promise<void> {
    if (professionalMemberId === undefined) {
      notify({ message: 'Scegli l’operatore.', variant: 'danger' })
      return
    }
    if (startsAt === '') {
      notify({ message: 'Scegli l’orario.', variant: 'danger' })
      return
    }
    const subject =
      subjectKind === 'member'
        ? gymMemberId === undefined
          ? null
          : { gymMemberId }
        : leadId === undefined
          ? null
          : { leadId }
    if (subject === null) {
      notify({ message: 'Scegli chi ha l’appuntamento.', variant: 'danger' })
      return
    }
    const start = new Date(startsAt)
    try {
      await mutations.create.mutateAsync({
        kind,
        professionalMemberId,
        ...subject,
        startsAt: start.toISOString(),
        endsAt: new Date(start.getTime() + durationMinutes * 60_000).toISOString(),
        ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
      })
      notify({ message: 'Fissato.', variant: 'success' })
      onClose()
    } catch (cause) {
      // Il `409` del server arriva con dentro il motivo e l'identificativo di ciò che
      // occupa quel tempo: mostrarlo così com'è dice più di «orario non disponibile».
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fissa un appuntamento</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Operatore">
            {(props) => (
              <Select
                {...props}
                value={professionalMemberId}
                onValueChange={setProfessional}
                aria-label="Operatore"
                placeholder="Scegli"
                items={staff.map((member) => ({
                  value: member.id,
                  label: (
                    <span className="inline-flex items-center gap-2">
                      {member.label}
                      <span className="text-muted-foreground text-xs">{member.role}</span>
                    </span>
                  ),
                }))}
              />
            )}
          </Field>

          <Field label="Tipo">
            {(props) => (
              <Select
                {...props}
                value={kind}
                onValueChange={(value) => setKind(value as AppointmentKind)}
                aria-label="Tipo di appuntamento"
                items={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
              />
            )}
          </Field>

          <Field label="Durata" hint="Minuti.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min={5}
                max={480}
                step={5}
                value={String(durationMinutes)}
                onChange={(event) => setDuration(Number(event.target.value))}
              />
            )}
          </Field>

          <Field label="Con chi">
            {(props) => (
              <Select
                {...props}
                value={subjectKind}
                onValueChange={(value) => setSubjectKind(value === 'lead' ? 'lead' : 'member')}
                aria-label="Iscritto o contatto"
                items={[
                  { value: 'member', label: 'Un iscritto' },
                  {
                    value: 'lead',
                    label: (
                      <span className="inline-flex items-center gap-2">
                        Un contatto
                        <span className="text-muted-foreground text-xs">prova gratuita</span>
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </Field>

          {subjectKind === 'member' ? (
            <Field label="Iscritto">
              {(props) => (
                <Select
                  {...props}
                  value={gymMemberId}
                  onValueChange={setGymMember}
                  aria-label="Iscritto"
                  placeholder="Scegli"
                  items={(members.data?.data ?? []).slice(0, 100).map((row) => ({
                    value: row.id,
                    label: `${row.lastName} ${row.firstName}`,
                  }))}
                />
              )}
            </Field>
          ) : (
            <Field label="Contatto">
              {(props) => (
                <Select
                  {...props}
                  value={leadId}
                  onValueChange={setLead}
                  aria-label="Contatto"
                  placeholder="Scegli"
                  items={(leads.data?.data ?? []).map((row) => ({
                    value: row.id,
                    label: (
                      <span className="inline-flex items-center gap-2">
                        {`${row.lastName} ${row.firstName}`}
                        <span className="text-muted-foreground text-xs">{row.status}</span>
                      </span>
                    ),
                  }))}
                />
              )}
            </Field>
          )}

          {professionalMemberId !== undefined ? (
            <div>
              <p className="text-muted-foreground mb-1 text-xs">
                Spazi liberi nei prossimi 14 giorni — li calcola il server, e togliono anche le
                lezioni che l’operatore insegna.
              </p>
              {slots.isPending ? (
                <Skeleton className="h-16 w-full" />
              ) : (slots.data?.data ?? []).length === 0 ? (
                <EmptyState
                  icon={<CalendarDays />}
                  title="Nessuno spazio libero"
                  description="Nessuna fascia libera con questa durata: scegli l’orario a mano qui sotto, la disponibilità propone e non vieta."
                />
              ) : (
                <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                  {(slots.data?.data ?? []).slice(0, 60).map((slot) => (
                    <Button
                      key={slot.startsAt}
                      size="sm"
                      variant={
                        startsAt === localInputValue(new Date(slot.startsAt))
                          ? 'primary'
                          : 'outline'
                      }
                      onClick={() => setStartsAt(localInputValue(new Date(slot.startsAt)))}
                    >
                      {new Date(slot.startsAt).toLocaleDateString('it-IT', {
                        weekday: 'short',
                        day: 'numeric',
                      })}{' '}
                      {hhmm(slot.startsAt)}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <Field label="Inizio" hint="Modificabile: fuori orario si può, il server lo accetta.">
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
              />
            )}
          </Field>

          <Field label="Nota" hint="Cosa serve sapere prima.">
            {(props) => (
              <Input
                {...props}
                value={notes}
                maxLength={2000}
                placeholder="Viene con il figlio"
                onChange={(event) => setNotes(event.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={mutations.create.isPending}
            onClick={() => void submit()}
          >
            Fissa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
